import classNames from "classnames";

import type { MeasureOverlayProjection } from "./BabylonManager";
import { formatMeters } from "./formatMeters";
import styles from "./MeasureOverlay.module.css";

/**
 * Points closer to the top edge than this get their label hung below them
 * instead of above. The overlay clips at the canvas box, so a label lifted
 * above a near-edge midpoint would be cut off entirely.
 */
const LABEL_FLIP_MARGIN_PX = 32;

interface MeasureOverlayProps {
  /** The projected overlay reported by the manager; `null` renders nothing. */
  overlay: MeasureOverlayProjection | null;
  /** Removes the one measurement whose label's "x" was clicked. */
  onRemove: (id: number) => void;
}

/**
 * The measure tool's screen-space layer: a distance label at each completed
 * measurement's projected midpoint, plus a dashed rubberband from the
 * anchor to the cursor while a measurement is in progress. The 3D lines and
 * dots live in the Babylon scene (`renderMeasures`); this layer holds only
 * what reads better as DOM — text in the app's typography, and a line that
 * ends at the cursor rather than at a picked point. Points behind the
 * camera arrive with `visible: false` and draw nothing.
 *
 * Each label carries its measurement's id, so overlapping labels can be
 * told apart and each one's "x" says which measurement it removes.
 *
 * The "x" is the only thing here that takes pointer events; everything else
 * lets them through to the canvas underneath. A click on it therefore
 * targets the button, and never also lands on the canvas as a pin. The
 * caller anchors this layer in the same positioned box as the canvas, and
 * every coordinate is canvas-relative CSS px.
 */
export function MeasureOverlay({ overlay, onRemove }: MeasureOverlayProps) {
  if (!overlay) {
    return null;
  }
  const { anchor, cursor, labels } = overlay;
  return (
    <div className={styles.overlay}>
      {anchor?.visible && cursor && (
        <svg className={styles.rubberband}>
          <line
            className={styles.rubberbandLine}
            x1={anchor.screenX}
            y1={anchor.screenY}
            x2={cursor.screenX}
            y2={cursor.screenY}
          />
        </svg>
      )}
      {labels
        .filter((label) => label.point.visible)
        .map((label) => (
          <div
            key={label.id}
            className={classNames(styles.label, {
              [styles.labelBelow]: label.point.screenY < LABEL_FLIP_MARGIN_PX,
            })}
            style={{ left: label.point.screenX, top: label.point.screenY }}
          >
            <span>{`M${label.id}: ${formatMeters(label.lengthMeters)}`}</span>
            <button
              aria-label={`Remove measurement M${label.id}`}
              className={styles.remove}
              onClick={() => onRemove(label.id)}
              title={`Remove measurement M${label.id}`}
              type="button"
            >
              ×
            </button>
          </div>
        ))}
    </div>
  );
}
